import EnforcementNoticeService from "@publicPortal/services/EnforcementNoticeService";
import { Serializer } from "@shared/mirage/helpers/Serializer";
import { memo, useCallback, useEffect } from "react";
import { EnforcementNoticeSearchState } from "./EnforcementNoticeSearchState";
import EnforcementNoticeSimpleSearch from "./EnforcementNoticeSimpleSearch";
import { EntityTableStateStorage } from "@mirage/components/entityTable/EntityTableStateStorage";
import PublicPortalState from "@publicPortal/utils/InitialState";
import { EnforcementNoticeFilter } from "@shared/publicportal/contracts/EnforcementNoticeFilter";
import { atom, useRecoilState } from "recoil";
import SearchTypeSwitchingTabs from "@publicPortal/components/advancedSearch/SearchTypeSwitchingTabs";
import SearchActions from "../SearchActions";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { SearchType } from "@shared/publicportal/contracts/Search";
import EnforcementNoticeAdvancedSearch from "@publicPortal/components/advancedSearch/EnforcementNoticeAdvancedSearch";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { PublicPortalMapSearch } from "@publicPortal/components/mapSearch/PublicPortalMapSearch";

const enfNoticeService = new EnforcementNoticeService();
const { initialSearchState, initialFilterState } = PublicPortalState.enforcementNoticeInitialSearchState();
const enforcementNoticesSearchState = atom({
    key: "enforcementNoticesSearchState",
    default: initialSearchState,
});
const pp = new PublicPortalRoutingService();

const EnforcementNoticeSearch = memo(() => {
    const navigate = useStableNavigate();
    const [state, setState] = useRecoilState<EnforcementNoticeSearchState>(enforcementNoticesSearchState);
    const onSearchTypeChanged = useCallback(
        (newSearchType: SearchType) => {
            setState((prev) => {
                return { ...prev, searchType: newSearchType };
            });
        },
        [setState]
    );

    useEffect(() => {
        const getFilteringLists = async () => {
            const filter = await enfNoticeService.getFilteringLists();
            setState((prev) => {
                return { ...prev, noticeTypes: filter?.NoticeType || [], parishes: filter?.Parishes || [], wards: filter?.Wards || [] };
            });
        };
        getFilteringLists();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setStateModifier = useCallback(
        (modifier: Partial<EnforcementNoticeFilter>) => {
            setState((prev) => {
                return {
                    ...prev,
                    filter: { ...prev.filter, ...modifier },
                };
            });
        },
        [setState]
    );
    const prepareFilter = useCallback(() => {
        switch (state.searchType) {
            case "Simple": {
                return { searchType: state.searchType, noticeRef: state.filter.noticeRef };
            }
            case "Advanced": {
                return { ...state.filter, searchType: state.searchType };
            }
            case "Map": {
                return { searchType: state.searchType, geometry: state.filter.geometry };
            }
            default:
                return null;
        }
    }, [state.filter, state.searchType]);
    const onClear = useCallback(() => {
        setState((prev) => {
            return {
                ...prev,
                filter: {
                    ...initialFilterState,
                },
            };
        });
    }, [setState]);
    const onSubmit = useCallback(() => {
        const filter = prepareFilter();
        const searchParams = Serializer.toQueryParamsNotEmptyOnly(filter);
        EntityTableStateStorage.clear("enforcementNotices");
        navigate(`${pp.SearchRoute("EnforcementNotices", searchParams)}`);
    }, [navigate, prepareFilter]);

    return (
        <>
            <SearchTypeSwitchingTabs searchType={state.searchType} onSearchTypeChanged={onSearchTypeChanged} />
            {getForm()}
            <SearchActions onCancelHandler={onClear} onSubmitHandler={onSubmit} />
        </>
    );

    function getForm() {
        switch (state.searchType) {
            case "Simple": {
                return <EnforcementNoticeSimpleSearch state={state} setter={setStateModifier} />;
            }
            case "Advanced": {
                return <EnforcementNoticeAdvancedSearch state={state} setter={setStateModifier} />;
            }
            case "Map": {
                return <PublicPortalMapSearch setStateModifier={setStateModifier} geometry={state.filter.geometry} />;
            }
            default:
                return null;
        }
    }
});

export default EnforcementNoticeSearch;
